import * as R from "remeda";
import { ChatBotAnalysisKeywordsButton } from "@/AnalysisKeywords/ChatBotAnalysisKeywords"
import { BotProviderType, ChatBotHistoryGroupWithUserSettings, ChatBotHistoryUserSettingSearch, getApiBotProviderActionsGetUserProfile, getGetApiBotProviderGetQueryKey, useGetApiBotProviderGet, useGetApiBotProviderUserSettingsGetBotProviderTags, useGetApiWorkspaceGet, usePostApiChatBotHistorySearchWithUserSettings } from "@/API"
import { DateToTimestamp, TimestampToDate } from "@/Helpers/DateHelper";
import { ActionIcon, Avatar, Badge, Box, Image, Button, Group, LoadingOverlay, Pagination, TagsInput, Text, Switch, Stack, TextInput } from "@mantine/core"
import { DateTimePicker } from '@mantine/dates';
import { Link, useNavigate } from "@tanstack/react-router"
import { DataTable } from "mantine-datatable"
import { useEffect, useState } from "react"
import { useImmer } from "use-immer"
import { PageTitle } from "@/Components/PageTitle";
import { AppBreadcrumbs } from "@/Components/AppBreadcrumbs";
import { IconEdit, IconFileExport, IconUser } from "@tabler/icons-react";
import { BotProviderUserSettingsButton } from "./BotProviderUserSettingsButton";
import { ChatBotHistoryExportModal } from "./ChatBotHistoryExportModal";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";

export function ChatBotHistoryPage(props: { workspaceId: string, botProviderId?: string, chatBotDataId?: string, provider?: string, botId?: string }) {
    const navigate = useNavigate()
    const workspace = useGetApiWorkspaceGet({ id: props.workspaceId })

    const [activePage, setPage] = useState(1);
    const PAGE_SIZE = 20
    const search_Mutation = usePostApiChatBotHistorySearchWithUserSettings()
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState<ChatBotHistoryGroupWithUserSettings[]>([])
    const [total, setTotal] = useState(0)
    const [startTime, setStartTime] = useState<string | null>(null);
    const [endTime, setEndTime] = useState<string | null>(null);
    const [search, setSearch] = useImmer<ChatBotHistoryUserSettingSearch>({
        botProviderId: props.botProviderId,
        chatBotDataId: props.chatBotDataId,
        provider: props.provider as BotProviderType,
        botId: props.botId
    })
    const botProvider = useGetApiBotProviderGet({ workspaceId: props.workspaceId, id: props.botProviderId }, {
        query: {
            queryKey: getGetApiBotProviderGetQueryKey({ workspaceId: props.workspaceId, id: props.botProviderId }),
            enabled: !!props.botProviderId
        }
    })
    const tagsList = useGetApiBotProviderUserSettingsGetBotProviderTags({ workspaceId: props.workspaceId, botProviderId: props.botProviderId })
    const [userProfileLoading, setUserProfileLoading] = useState(false)
    const [searchExportModalOpened, setSearchExportModalOpened] = useState(false)
    const [rowExportTarget, setRowExportTarget] = useState<{ botProviderId?: string; userId?: string; userName?: string } | null>(null)

    useEffect(() => {
        ExecuteSearch()
    }, [activePage, startTime, endTime, search.userSettingTags, search.disableReply])
    function ExecuteSearch() {
        setLoading(true)
        search_Mutation.mutateAsync({
            data: {
                ...search,
                startTimestamp: startTime ? DateToTimestamp(new Date(startTime)) : undefined,
                endTimestamp: endTime ? DateToTimestamp(new Date(endTime)) : undefined,
            },
            params: {
                recordsLimit: 1,
                skip: (activePage - 1) * PAGE_SIZE, limit: PAGE_SIZE
            }
        }).then(x => {
            setData(x.data.results ?? [])
            setTotal(x.data.total ?? 0)
        }).finally(() => {
            setLoading(false)
        })
    }
    return <Box pos="relative">
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
        <AppBreadcrumbs
            items={[
                {
                    label: '工作空間列表',
                    linkProps: { to: '/workspace' },
                },
                {
                    label: workspace.data?.data.name ?? '',
                    linkProps: { to: '/workspace/$workspaceId', params: { workspaceId: props.workspaceId } },
                },
                {
                    label: '機器人串接',
                    linkProps: { to: '/workspace/$workspaceId/botProvider', params: { workspaceId: props.workspaceId } },
                },
                {
                    label: (botProvider.data?.data.name ?? "") + "聊天紀錄",
                    active: true,
                },
            ]}
        />
        <Group mt="sm">
            <ChatBotAnalysisKeywordsButton search={search} />
            <Button
                leftSection={<IconFileExport size={16} />}
                variant="default"
                onClick={() => setSearchExportModalOpened(true)}
            >
                匯出搜尋結果
            </Button>
        </Group>
        <form>
            <Stack gap={5} mt="sm">
                <Group>
                    <Text>開始時間</Text>
                    <DateTimePicker w="200" valueFormat="YYYY/MM/DD HH:mm" value={startTime} onChange={setStartTime} clearable />
                    <Text>結束時間</Text>
                    <DateTimePicker w="200" valueFormat="YYYY/MM/DD HH:mm" value={endTime} onChange={setEndTime} clearable />
                </Group>
                <Group>
                    <Text>名稱</Text>
                    <TextInput w="200" value={search.userName ?? ""} onChange={v => {
                        setSearch(x => {
                            x.userName = v.target.value
                        })
                    }} />
                    <Text>聊天室Tags</Text>
                    <TagsInput data={tagsList.data?.data} value={search.userSettingTags} onChange={v => {
                        setSearch(x => {
                            x.userSettingTags = v
                        })
                    }} />
                    <Text>聊天紀錄Tags</Text>
                    <TagsInput data={tagsList.data?.data} value={search.tags} onChange={v => {
                        setSearch(x => {
                            x.tags = v
                        })
                    }} />
                </Group>
                <Switch
                    label="只顯示已關閉自動回應"
                    checked={!!search.disableReply}
                    onChange={v => {
                        setSearch(x => {
                            if (v.target.checked) x.disableReply = true
                            else x.disableReply = null
                        })
                    }}
                />
            </Stack>
            <Button my="md" loading={loading} type="submit" onClick={ExecuteSearch}>
                搜尋
            </Button>
        </form>
        <Pagination value={activePage} onChange={setPage} total={Math.ceil(total / PAGE_SIZE)} />
        <DataTable
            withTableBorder
            minHeight={data.length > 0 ? 0 : 150}
            noRecordsText="沒有資料"
            columns={[
                { accessor: 'key.userId', title: "UserId" },
                { accessor: 'name', title: "名稱", render: (data) => data.userSettings?.name ?? "" },
                {
                    accessor: 'timestamp', title: "最後更新", render: (data) => <>
                        {TimestampToDate(R.last(data.items ?? [])?.createdTimestamp ?? 0).toLocaleString()}
                    </>
                },
                {
                    accessor: 'DisableReply', title: "關閉自動回應", render: (data) => <>
                        {data.userSettings?.disableReply ? <Text c="red">關閉中</Text> : <Text>無</Text>}
                    </>
                },
                {
                    accessor: 'historyStartTimestamp', title: "歷史起點", render: (data) => <>
                        {data.userSettings?.historyStartTimestamp && data.userSettings.historyStartTimestamp > 0
                            ? <Badge size="xs" color="orange" variant="light">{TimestampToDate(data.userSettings.historyStartTimestamp).toLocaleString()}</Badge>
                            : <Text size="xs" c="dimmed">無</Text>}
                    </>
                },
                {
                    accessor: 'tags', title: "Tags", render: (data) => <>
                        {data.userSettings?.tags?.length == 0 ? <Text>無</Text> : data.userSettings?.tags?.map((x, idx) => <Button key={idx} size="compact-xs" radius="lg" style={{ textTransform: 'none' }} onClick={() => {
                            setSearch(s => {
                                s.userSettingTags = R.unique([...(s.userSettingTags ?? []), x])
                            })
                        }}>{x}</Button>)}
                    </>
                },
                {
                    accessor: "question", title: "最近問題", render: (data) => data.items?.flatMap(x => x.records)?.filter(x => x?.role == "User").map((d, idx) => {
                        var t = d?.message ?? "";
                        if (t.length > 10) t = t?.slice(0, 10) + "..."
                        return t
                    }).join(" | ")
                },
                {
                    accessor: 'actions',
                    title: <Box mr={6}>操作</Box>,
                    textAlign: 'right',
                    render: (data) => {
                        return (
                            <Group gap={4} justify="right" wrap="nowrap">
                                <ActionIcon loading={userProfileLoading} size="sm" variant="subtle" onClick={async () => {
                                    setUserProfileLoading(true)
                                    try {
                                        var result = await getApiBotProviderActionsGetUserProfile({ workspaceId: props.workspaceId, botProviderId: data.key?.botProviderId, userId: data.key?.userId })
                                        modals.open({
                                            title: '使用者資訊',
                                            children: (
                                                <>
                                                    <Group>
                                                        <Avatar size="xl" src={result.data.imageUrl} style={{
                                                            cursor: "pointer"
                                                        }} onClick={() => {
                                                            modals.open({
                                                                title: "使用者圖片",
                                                                children: (
                                                                    <>
                                                                        <Image
                                                                            radius="md"
                                                                            src={result.data.imageUrl}
                                                                            h={200}
                                                                            fallbackSrc="https://placehold.co/600x400?text=NoImage"
                                                                        />
                                                                    </>
                                                                ),
                                                            });
                                                        }} />
                                                        <Text fw={600} c="var(--mantine-primary-color-filled)" style={{ fontSize: "30px" }}>
                                                            {result.data.name}
                                                        </Text>
                                                    </Group>
                                                </>
                                            ),
                                        });
                                    } catch (e) {
                                        notifications.show({ message: "取得使用者資訊失敗", color: "red" })
                                    }
                                    setUserProfileLoading(false)
                                }}>
                                    <IconUser size={16} />
                                </ActionIcon>
                                {
                                    data.key?.botProviderId && data.key?.userId && <BotProviderUserSettingsButton workspaceId={props.workspaceId} botProviderId={data.key?.botProviderId} userId={data.key?.userId} onUpdate={() => {
                                        ExecuteSearch()
                                    }}>
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            title="使用者設定"
                                        >
                                            <IconEdit size={16} />
                                        </ActionIcon>
                                    </BotProviderUserSettingsButton>
                                }
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    title="匯出此使用者聊天紀錄"
                                    onClick={() => {
                                        setRowExportTarget({
                                            botProviderId: data.key?.botProviderId,
                                            userId: data.key?.userId,
                                            userName: data.userSettings?.name ?? undefined,
                                        })
                                    }}
                                >
                                    <IconFileExport size={16} />
                                </ActionIcon>
                            </Group>
                        );
                    },
                },
            ]}
            onRowDoubleClick={({ record }) => {
                navigate({
                    to: "/workspace/$workspaceId/botProvider/$botProviderId/user/$userId", params: {
                        workspaceId: props.workspaceId,
                        botProviderId: record.key?.botProviderId ?? "",
                        userId: record.key?.userId ?? ""
                    }
                })
            }}
            records={data.map(x => {
                return {
                    id: x.key?.botProviderId + "_" + x.key?.userId,
                    ...x
                }
            }) ?? []}
        />

        <ChatBotHistoryExportModal
            opened={searchExportModalOpened}
            onClose={() => setSearchExportModalOpened(false)}
            defaultSearch={{
                ...search,
                startTimestamp: startTime ? DateToTimestamp(new Date(startTime)) : undefined,
                endTimestamp: endTime ? DateToTimestamp(new Date(endTime)) : undefined,
            }}
            title="匯出聊天紀錄（依目前搜尋條件）"
        />

        {rowExportTarget && (
            <ChatBotHistoryExportModal
                opened={!!rowExportTarget}
                onClose={() => setRowExportTarget(null)}
                defaultSearch={{
                    botProviderId: rowExportTarget.botProviderId,
                    userId: rowExportTarget.userId,
                }}
                title={`匯出使用者 ${rowExportTarget.userName || rowExportTarget.userId} 聊天紀錄`}
            />
        )}
    </Box>
}